import React, { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { api } from '@/services/api'
import { dashboardService } from '@/services/dashboardService'

// Icons
const ClipboardIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
)

const ClockIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const CheckCircleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const ExclamationIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
  </svg>
)

// Complaint Card Component
const ComplaintCard = ({ complaint, onUpdateStatus }) => {
  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in_progress': 'bg-blue-100 text-blue-800',
      'resolved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-red-100 text-red-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    }
    return colors[priority] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{complaint.title}</h3>
          <p className="text-sm text-gray-500">#{complaint.id}</p>
        </div>
        <div className="flex space-x-2">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(complaint.status)}`}>
            {complaint.status.replace('_', ' ').toUpperCase()}
          </span>
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(complaint.priority)}`}>
            {complaint.priority.toUpperCase()}
          </span>
        </div>
      </div>
      
      <p className="text-gray-600 mb-4">{complaint.description}</p>
      
      <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
        <span>By: {complaint.complainant_name}</span>
        <span>{complaint.created_at}</span>
      </div>
      
      <div className="flex space-x-2">
        {complaint.status === 'pending' && (
          <Button
            size="sm"
            variant="primary"
            onClick={() => onUpdateStatus(complaint.id, 'in_progress')}
          >
            Start Work
          </Button>
        )}
        {complaint.status === 'in_progress' && (
          <Button
            size="sm"
            variant="primary"
            onClick={() => onUpdateStatus(complaint.id, 'resolved')}
          >
            Mark Resolved
          </Button>
        )}
        <Button size="sm" variant="outline">
          View Details
        </Button>
      </div>
    </div>
  )
}

// Main Staff Dashboard Component
const StaffDashboard = () => {
  const { user } = useAuthStore()
  const [complaints, setComplaints] = useState([])
  const [stats, setStats] = useState(null)
  const [departments, setDepartments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('all')

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true)
      try {
        // Fetch stats and departments in parallel
        const [statsResponse, departmentsResponse, complaintsResponse] = await Promise.all([
          dashboardService.getStats().catch(() => null),
          dashboardService.getMyDepartments().catch(() => []),
          api.get('/complaints').catch(() => ({ data: { data: [] } }))
        ])

        if (statsResponse) {
          setStats(statsResponse)
        }
        
        if (departmentsResponse) {
          setDepartments(departmentsResponse)
        }

        if (complaintsResponse?.data?.data) {
          setComplaints(complaintsResponse.data.data)
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (user) {
      fetchDashboardData()
    }
  }, [user])

  const handleUpdateStatus = async (complaintId, newStatus) => {
    try {
      const response = await fetch(`/api/v1/complaints/${complaintId}/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      })

      if (response.ok) {
        const result = await response.json()
        // Update the complaint in the list
        setComplaints(complaints.map(complaint => 
          complaint.id === complaintId 
            ? { ...complaint, status: newStatus }
            : complaint
        ))
        alert('Status updated successfully!')
      } else {
        const error = await response.json()
        alert('Error updating status: ' + (error.message || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error updating status:', error)
      alert('Error updating status. Please try again.')
    }
  }

  const filteredComplaints = complaints.filter(complaint => {
    if (activeTab === 'all') return true
    return complaint.status === activeTab
  })

  const dashboardStats = stats ? [
    {
      title: 'Total Complaints',
      value: stats.total,
      icon: ClipboardIcon,
      color: 'bg-blue-500'
    },
    {
      title: 'Pending',
      value: stats.pending,
      icon: ClockIcon,
      color: 'bg-yellow-500'
    },
    {
      title: 'In Progress',
      value: stats.in_progress,
      icon: ExclamationIcon,
      color: 'bg-orange-500'
    },
    {
      title: 'Resolved',
      value: stats.resolved,
      icon: CheckCircleIcon,
      color: 'bg-green-500'
    }
  ] : []

  const tabs = [
    { id: 'all', label: 'All Complaints', count: complaints.length },
    { id: 'pending', label: 'Pending', count: complaints.filter(c => c.status === 'pending').length },
    { id: 'in_progress', label: 'In Progress', count: complaints.filter(c => c.status === 'in_progress').length },
    { id: 'resolved', label: 'Resolved', count: complaints.filter(c => c.status === 'resolved').length }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {user?.role === 'committee_member' ? 'Committee Member' : 'Staff'} Dashboard
          </h1>
          <p className="mt-2 text-gray-600">
            Welcome back, {user?.first_name}! Manage complaints for your assigned departments.
          </p>
          {departments.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-sm text-gray-500">Assigned Departments:</span>
              {departments.map((dept) => (
                <span key={dept.id} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {dept.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {dashboardStats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-full ${stat.color}`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Complaints Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Complaints Management</h2>
          </div>
          
          {/* Tabs */}
          <div className="px-6 py-4 border-b border-gray-200">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </nav>
          </div>

          {/* Complaints List */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredComplaints.map((complaint) => (
                  <ComplaintCard
                    key={complaint.id}
                    complaint={complaint}
                    onUpdateStatus={handleUpdateStatus}
                  />
                ))}
              </div>
            )}
            
            {!isLoading && filteredComplaints.length === 0 && (
              <div className="text-center py-8">
                <ClipboardIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No complaints found for this category.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StaffDashboard

